import { useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import type { User } from 'firebase/auth';
import { ChevronDown, LogOut, Settings as SettingsIcon } from 'lucide-react';
import { signOutUser } from '../lib/firebase';
import { useRole } from '../lib/useRole';

function initials(user: User): string {
  const source = user.displayName || user.email || '?';
  const parts = source.replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase();
}

export default function UserMenu({ user }: { user: User }) {
  const [open, setOpen] = useState(false);
  const role = useRole();
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = user.displayName || user.email?.replace(/@.*$/, '') || 'Account';

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className={`flex h-9 cursor-pointer items-center gap-2 rounded-full border py-0 pl-1 pr-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/70 sm:pr-2.5 ${
          open
            ? 'border-neutral-700 bg-neutral-800'
            : 'border-neutral-800 bg-neutral-900/60 hover:border-neutral-700 hover:bg-neutral-800/70'
        }`}
      >
        {user.photoURL ? (
          <img src={user.photoURL} alt="" className="h-7 w-7 rounded-full object-cover" />
        ) : (
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary-300 to-secondary-400 text-[11px] font-bold text-neutral-950">
            {initials(user)}
          </span>
        )}
        <span className="hidden max-w-[8rem] truncate text-sm text-neutral-300 sm:block">{name}</span>
        <ChevronDown
          className={`hidden h-3.5 w-3.5 text-neutral-500 transition-transform sm:block ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+0.5rem)] w-64 overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.8)] ring-1 ring-neutral-800/60 motion-safe:animate-[popIn_130ms_cubic-bezier(0.2,0.9,0.3,1)]"
        >
          <div className="border-b border-neutral-800 px-3.5 py-3">
            <p className="truncate text-sm font-medium text-neutral-100">{name}</p>
            <p className="truncate text-xs text-neutral-500">{user.email}</p>
            {!role.loading && (
              <span className="mt-2 inline-block rounded-full border border-primary-300/30 bg-primary-300/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-300">
                {role.role ?? 'no role'}
              </span>
            )}
          </div>
          <div className="p-1.5">
            <Link
              href="/settings"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-neutral-300 transition-colors hover:bg-neutral-800 hover:text-neutral-50"
            >
              <SettingsIcon className="h-4 w-4 text-neutral-500" /> Settings
            </Link>
            <button
              role="menuitem"
              onClick={() => void signOutUser()}
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-neutral-300 transition-colors hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-300"
            >
              <LogOut className="h-4 w-4 text-neutral-500" /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
